import { DefaultService, OpenAPI } from "../api";
import type { Athlete, AuthResponse, Activity as GeneratedActivity } from "../api";
import { request as apiRequest } from "../api/core/request";
import type { Gear } from "../api/models/Gear";
import { authDebug } from "./AuthDebug";
import { AuthService } from "./AuthService";
import { localizeDistance, localizePace, localizeSpeed } from "./Formatters";
import { urlEncode } from "./General";
import { getStravaRedirectUri } from "./StravaAuth";

const DEFAULT_API_BASE = "https://api.dev.activityviz.com";
const STRAVA_AUTH_EXCHANGE_TIMEOUT_MS = 15_000;

export const getActivityVizApiBase = (): string => {
  const configuredApiBase = import.meta.env.VITE_API_URL;
  if (typeof configuredApiBase === "string" && configuredApiBase.trim().length > 0) {
    return configuredApiBase.trim();
  }

  const legacyConfiguredApiBase = import.meta.env.VITE_API_BASE_URL;
  if (typeof legacyConfiguredApiBase === "string" && legacyConfiguredApiBase.trim().length > 0) {
    return legacyConfiguredApiBase.trim();
  }

  return DEFAULT_API_BASE;
};

const apiBase = getActivityVizApiBase();
const stravaClientId = 15183;
const stravaScope = "profile:read_all,activity:read_all";
export const AUTH_EXPIRED_EVENT = "activityviz:auth-expired";

// Configure the generated API client
OpenAPI.BASE = apiBase;

// Initialize OpenAPI with stored JWT token if available
const storedToken = AuthService.getToken();
if (storedToken && !AuthService.isTokenExpired()) {
  OpenAPI.TOKEN = storedToken;
}

authDebug("api.client.initialized", {
  apiBase: OpenAPI.BASE,
  redirectUri: typeof window === "undefined" ? null : getStravaRedirectUri(),
  auth: AuthService.getSnapshot(),
});

OpenAPI.ON_AUTH_ERROR = (result) => {
  const requestHadToken = typeof OpenAPI.TOKEN === "string" && OpenAPI.TOKEN.length > 0;
  authDebug("api.auth_error", {
    status: result.status,
    url: result.url,
    apiBase: OpenAPI.BASE,
    requestHadToken,
    authBeforeClear: AuthService.getSnapshot(),
  });

  // A request can race with auth hydration and be sent without its bearer token. Do not
  // turn that client-side omission into a cross-tab logout; a real authenticated 401 still
  // clears the session below.
  if (!requestHadToken) {
    authDebug("api.auth_error.ignored_missing_token", {
      status: result.status,
      url: result.url,
      auth: AuthService.getSnapshot(),
    });
    return;
  }

  AuthService.clearAuth();
  OpenAPI.TOKEN = undefined;

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(AUTH_EXPIRED_EVENT, {
        detail: {
          status: result.status,
          url: result.url,
        },
      }),
    );
  }
};

// Axios config and instance
export interface ActivityVizApiConfig {
  baseURL?: string;
  token?: string;
}

export const configureActivityVizApi = (config: ActivityVizApiConfig) => {
  if (config.baseURL) {
    OpenAPI.BASE = config.baseURL;
  }
  if (config.token) {
    OpenAPI.TOKEN = config.token;
  }
};

interface PagedResponse<T> {
  items: T[];
  next_key?: string | null;
  deletedIds: number[];
}

// Re-export generated types with old names for backward compatibility
export type StravaAthlete = Athlete;

// Extend AuthResponse to match the old ApiUser structure
export type ApiUser = AuthResponse & {
  token_type?: string;
  expires_in?: number;
  refresh_token?: string;
  access_token?: string;
  athlete?: StravaAthlete;
  unit: string;
};

type RawAuthResponse = Partial<AuthResponse> & {
  token_type?: string;
  expires_in?: number | string;
  refresh_token?: string;
  access_token?: string;
  athlete?: Partial<StravaAthlete> & {
    id?: number | string | null;
  };
  athlete_id?: number | string | null;
  jwt_token?: string | null;
  expires_at?: number | string | null;
};

export type Activity = GeneratedActivity;

const restorePersistedToken = (): boolean => {
  const persistedToken = AuthService.getToken();
  if (!persistedToken || AuthService.isTokenExpired()) {
    return false;
  }

  if (OpenAPI.TOKEN !== persistedToken) {
    authDebug("session.token_reapplied_before_activity_page", {
      apiBase: OpenAPI.BASE,
      auth: AuthService.getSnapshot(),
    });
    OpenAPI.TOKEN = persistedToken;
  }

  return true;
};

export type ActivityLocalized = Omit<
  GeneratedActivity,
  | "start_date"
  | "start_date_local"
  | "distance"
  | "total_elevation_gain"
  | "average_speed"
  | "max_speed"
> & {
  start_date: Date;
  start_date_local: Date;
  distance: number;
  total_elevation_gain: number;
  average_speed: number;
  max_speed: number;
  speed: number;
  pace: number;
  gearName?: string;
};

type StreamData = {
  data: any[];
  original_size: number;
  resolution: string;
  series_type: string;
};

export type ActivityStreams = {
  altitude?: StreamData;
  cadence?: StreamData;
  velocity_smooth?: StreamData;
  distance?: StreamData;
  heartrate?: StreamData;
  latlng?: StreamData;
  temp?: StreamData;
  time?: StreamData;
  watts?: StreamData;
  grade_smooth?: StreamData;
};

export { Gear };

const resolveActivityGearName = (
  activity: GeneratedActivity,
  gearMap?: Record<string, Gear>,
): string | undefined => {
  if (activity.gear_id && gearMap?.[activity.gear_id]?.name) {
    return gearMap[activity.gear_id].name || undefined;
  }

  if (activity.gear && typeof activity.gear === "object" && "name" in activity.gear) {
    const gearName = activity.gear.name;
    if (typeof gearName === "string" && gearName.trim().length > 0) {
      return gearName;
    }
  }

  if (activity.raw?.gear && typeof activity.raw.gear === "object" && "name" in activity.raw.gear) {
    const gearName = activity.raw.gear.name;
    if (typeof gearName === "string" && gearName.trim().length > 0) {
      return gearName;
    }
  }

  return undefined;
};

export const localizeActivity = (
  a: GeneratedActivity,
  unit: string,
  gearMap?: Record<string, Gear>,
): ActivityLocalized => {
  return {
    ...a,
    start_date: new Date(a.start_date),
    start_date_local: new Date(a.start_date_local || a.start_date),
    distance: localizeDistance(a.distance || 0, unit, true),
    total_elevation_gain: localizeDistance(a.total_elevation_gain || 0, unit, false),
    average_speed: localizeSpeed(a.average_speed || 0, unit),
    max_speed: localizeSpeed(a.max_speed || 0, unit),
    speed: localizeSpeed(a.average_speed || 0, unit),
    pace: localizePace(a.average_speed || 0, unit),
    gearName: resolveActivityGearName(a, gearMap),
  };
};

export const getStravaAuthUrl = (): string => {
  const argStr = urlEncode({
    client_id: stravaClientId,
    response_type: "code",
    redirect_uri: getStravaRedirectUri(),
    scope: stravaScope,
    //approval_prompt: "auto",
  });
  return `https://www.strava.com/oauth/authorize?${argStr}`;
};

const coerceNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizeAuthResponse = (authResponse: RawAuthResponse): ApiUser => {
  const jwtToken =
    typeof authResponse.jwt_token === "string" && authResponse.jwt_token.trim().length > 0
      ? authResponse.jwt_token
      : typeof authResponse.access_token === "string" && authResponse.access_token.trim().length > 0
        ? authResponse.access_token
        : null;

  const athleteId =
    coerceNumber(authResponse.athlete_id) ?? coerceNumber(authResponse.athlete?.id ?? null);

  const expiresAt =
    coerceNumber(authResponse.expires_at) ??
    (() => {
      const expiresIn = coerceNumber(authResponse.expires_in);
      return expiresIn ? Math.floor(Date.now() / 1000) + expiresIn : null;
    })();

  if (!jwtToken || !athleteId || !expiresAt) {
    throw new Error(
      `Invalid auth response payload: hasJwt=${Boolean(jwtToken)} athleteId=${String(athleteId)} expiresAt=${String(expiresAt)}`,
    );
  }

  return {
    ...authResponse,
    jwt_token: jwtToken,
    athlete_id: athleteId,
    expires_at: expiresAt,
    unit: "imperial",
  };
};

const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  message: string,
): Promise<T> => {
  return await Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => {
        reject(new Error(message));
      }, timeoutMs);
    }),
  ]);
};

export const completeStravaAuth = async (code: string): Promise<ApiUser> => {
  authDebug("auth.exchange.start", {
    codeLength: code.length,
    apiBase: OpenAPI.BASE,
    redirectUri: getStravaRedirectUri(),
  });
  const authResponse = (await withTimeout(
    DefaultService.authCompleteStravaAuthCompleteGet(code),
    STRAVA_AUTH_EXCHANGE_TIMEOUT_MS,
    `Timed out contacting ${OpenAPI.BASE} while finishing Strava sign-in.`,
  )) as RawAuthResponse;
  authDebug("auth.exchange.raw_success", {
    hasJwtToken: Boolean(authResponse.jwt_token),
    hasAccessToken: Boolean(authResponse.access_token),
    athleteId: authResponse.athlete_id ?? authResponse.athlete?.id ?? null,
    expiresAt: authResponse.expires_at ?? null,
    expiresIn: authResponse.expires_in ?? null,
  });
  const normalizedResponse = normalizeAuthResponse(authResponse);
  authDebug("auth.exchange.success", {
    athleteId: normalizedResponse.athlete_id,
    expiresAt: normalizedResponse.expires_at,
    hasJwt: Boolean(normalizedResponse.jwt_token),
  });

  // Store the JWT token in localStorage
  AuthService.setAuth(
    normalizedResponse.jwt_token,
    normalizedResponse.expires_at,
    normalizedResponse.athlete_id,
  );
  authDebug("auth.exchange.persisted", {
    auth: AuthService.getSnapshot(),
  });

  // Configure the API client with the JWT token
  OpenAPI.TOKEN = normalizedResponse.jwt_token;

  return normalizedResponse;
};

export const getAthlete = (athleteId: number, force = false): Promise<StravaAthlete> => {
  return DefaultService.getStravaUserStravaAthleteGet(force);
};

export const getGear = (gearId: string): Promise<Gear> => {
  return apiRequest(OpenAPI, {
    method: "GET",
    url: "/strava/gear/{gear_id}",
    path: {
      gear_id: gearId,
    },
  });
};

export const getActivitiesPage = (
  athleteId: number,
  startKey?: string,
  updatedSince?: number,
): Promise<PagedResponse<Activity>> => {
  restorePersistedToken();
  return DefaultService.getActivitiesStravaActivitiesGet(200, updatedSince ?? null, startKey).then(
    (res) => ({
      items: res.items || [],
      next_key: res.next_key ? JSON.stringify(res.next_key) : null,
      deletedIds: res.deleted_activity_ids || [],
    }),
  );
};

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Generator to fetch all activities pages for an athlete, yielding each page as fetched.
 */
export async function* getAllActivitiesPages(
  athleteId: number,
  updatedSince?: number,
): AsyncGenerator<Activity[], void, unknown> {
  let nextKey: string | undefined = undefined;
  do {
    const page = await getActivitiesPage(athleteId, nextKey, updatedSince);
    yield page.items;
    nextKey = page.next_key ?? undefined;
  } while (nextKey);
}

export const getActivity = (athleteId: number, activityId: number): Promise<Activity> => {
  return DefaultService.getStravaActivityStravaActivityActivityIdGet(activityId, false);
};

export const fetchAllActivities = (_athleteId: number): Promise<any> => {
  return DefaultService.enqueueGetAllActivitiesStravaFetchAllActivitiesGet(1);
};

/**
 * Logout user by clearing stored authentication data
 */
export const logout = (): void => {
  AuthService.clearAuth();
  OpenAPI.TOKEN = undefined;
};

export const fetchAllStreams = (_athleteId: number): Promise<any> => {
  return DefaultService.enqueueGetAllActivityStreamsStravaFetchAllStreamsGet();
};

export const getActivityStreams = (
  athleteId: number,
  activityId: number,
): Promise<ActivityStreams> => {
  return DefaultService.getStravaActivityStreamsStravaActivityActivityIdStreamsGet(
    activityId,
    false,
  ) as Promise<any>;
};

// Export AuthService for use in components
export { AuthService };
